import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { ChangeEvent, JSX } from 'react';
import { IOntologyNode, ITreeViewRulesData, ITreeViewRule } from './DataModels';
import { SerendieSymbol } from '@serendie/symbols';
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler, RightButtons, Select, Switch, TextArea, TextField } from "@iconics/components";
import Helper from '../../../../Common/Helper';
import { Guid } from '@iconics/core';
import { CheckedChangeDetails } from '@ark-ui/react/dist/components/switch/switch';
import { SelectionDetails } from '@zag-js/select';

class VirtualTreeViewRulesEditor extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }

    // the last rules array we pushed to the parent; the parent stores it as-is
    // (definition.Data = { TreeViewRules: rules }), so when it flows back in as
    // props we can recognise it as our own update and avoid re-initialising.
    private lastEmittedRules?: ITreeViewRule[];

    private ruleTypes = [
    //    { label: this.labels.TreeViewRule_RuleTypes_Filter, value: 'Filter'}, not supported yet
        { label: this.labels.TreeViewRule_RuleTypes_Real, value: 'Real'},
    ];

    // we can add a new rule only if there is a node available beneath the
    // deepest assigned node (or any node when nothing is assigned yet).
    private get canAdd(): boolean
    {
        return this.getAssignableNodesForNewRule().length > 0;
    }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            selectedRule: undefined,
            rules: [],
            gridRefreshToken: Guid.new().toString(),
            gridData: { rows: [] },
            nodes: []
        };
    }

    public componentDidUpdate(prevProps: IProps): void
    {
        const ontologyNodesChanged = prevProps.ontologyNodes !== this.props.ontologyNodes;
        const rulesChanged = prevProps.rules !== this.props.rules;

        if (!ontologyNodesChanged && !rulesChanged)
            return;

        // ignore the round-trip of our own edits: the parent re-wraps the exact
        // rules array we emitted, so a matching reference means nothing external
        // actually changed and the editor is already in sync. Re-initialising
        // here would clear the selected rule and rebuild the whole structure.
        if (!ontologyNodesChanged && this.props.rules.TreeViewRules === this.lastEmittedRules)
            return;

        const nodes = this.props.ontologyNodes?.length > 0
            ? this.buildOntologyNodesList(this.props.ontologyNodes)
            : [];

        const rules = this.buildValidRules(this.props.rules.TreeViewRules, nodes);

        this.setState({
            nodes,
            rules,
            gridRefreshToken: Guid.new().toString(),
            selectedRule: undefined
        });
    }

    // propagate the current rules to the parent so the saved definition stays in
    // sync, while flagging the emitted array so the returning props update is
    // recognised as our own and does not trigger a re-initialisation.
    private raiseDefinitionUpdate(rules: ITreeViewRule[] = this.state.rules): void
    {
        this.lastEmittedRules = rules;
        this.props.onDefinitionUpdate(rules);
    }

    private buildValidRules(sourceRules: ITreeViewRule[], nodes: IOrderedOntologyNode[]): ITreeViewRule[]
    {
        const nodeLevelById = new Map<string, number>(nodes.map(n => [n.Id, n.Level]));
        const seenLevels = new Set<number>();

        const validRules: ITreeViewRule[] = [];
        sourceRules
            .slice()
            .forEach(rule => {
                const level = nodeLevelById.get(rule.OntologyNodeId);
                if (level === undefined || seenLevels.has(level))
                    return;

                seenLevels.add(level);
                validRules.push({
                    ...rule,
                    Id: Guid.new().toString(),
                    Order: validRules.length
                });
            });

        return validRules;
    }

    // build an ordered list from an arbitrary forest (multiple roots, any number of children)
    private buildOntologyNodesList(nodes: IOntologyNode[]): IOrderedOntologyNode[]
    {
        const orderedList: IOrderedOntologyNode[] = [];
        const nodeById = new Map<string, IOntologyNode>(nodes.map(node => [node.Id, node]));
        const parentToChildren = new Map<string | null, IOntologyNode[]>();

        const addChild = (parentId: string | null, child: IOntologyNode) => {
            const list = parentToChildren.get(parentId) ?? [];
            list.push(child);
            parentToChildren.set(parentId, list);
        };

        nodes.forEach(node => {
            const parentId = node.Parent_ID && nodeById.has(node.Parent_ID)
                ? node.Parent_ID
                : null;
            addChild(parentId, node);
        });

        const sortByName = (a: IOntologyNode, b: IOntologyNode) => a.Name.localeCompare(b.Name);
        const visited = new Set<string>();

        const visit = (node: IOntologyNode, level: number, parentId: string | null) => {
            if (visited.has(node.Id))
                return;

            visited.add(node.Id);
            orderedList.push({
                Id: node.Id,
                Name: node.Name,
                Level: level,
                ParentId: parentId
            });

            const children = (parentToChildren.get(node.Id) ?? []).slice().sort(sortByName);
            children.forEach(child => visit(child, level + 1, node.Id));
        };

        const roots = (parentToChildren.get(null) ?? []).slice().sort(sortByName);
        roots.forEach(root => visit(root, 0, null));

        nodes.forEach(node => {
            if (!visited.has(node.Id))
                visit(node, 0, null);
        });

        return orderedList;
    }

    private getChildren(parentId: string | null): IOrderedOntologyNode[]
    {
        return this.state.nodes.filter(node => node.ParentId === parentId);
    }

    // true when ancestorId is a proper ancestor of nodeId (walking up ParentId).
    private isAncestor(ancestorId: string, nodeId: string): boolean
    {
        const parentById = new Map<string, string | null>(this.state.nodes.map(n => [n.Id, n.ParentId]));

        let current = parentById.get(nodeId) ?? null;
        while (current)
        {
            if (current === ancestorId)
                return true;
            current = parentById.get(current) ?? null;
        }
        return false;
    }

    // the assigned node sitting furthest down the tree; a new rule can only be
    // added beneath this node. Optionally ignore a rule (used while editing it).
    private getDeepestAssignedNode(excludeRuleId?: string): IOrderedOntologyNode | undefined
    {
        const nodeById = new Map<string, IOrderedOntologyNode>(this.state.nodes.map(n => [n.Id, n]));

        let deepest: IOrderedOntologyNode | undefined;
        this.state.rules.forEach(rule => {
            if (excludeRuleId && rule.Id === excludeRuleId)
                return;

            const node = nodeById.get(rule.OntologyNodeId);
            if (node && (deepest === undefined || node.Level > deepest.Level))
                deepest = node;
        });

        return deepest;
    }

    // nodes a brand-new rule could be assigned to: the children of the deepest
    // assigned node, or every node when no rule has been assigned yet. A leaf
    // being the deepest assigned node yields an empty list (nothing to add).
    private getAssignableNodesForNewRule(): IOrderedOntologyNode[]
    {
        const deepest = this.getDeepestAssignedNode();
        if (!deepest)
            return this.state.nodes;

        return this.getChildren(deepest.Id);
    }

    private getAvailableNodes(): {value: string, label: string}[]
    {
        const { selectedRule } = this.state;
        if (!selectedRule)
            return [];

        const orderedRules = this.state.rules.slice().sort((a, b) => a.Order - b.Order);
        const selectedIndex = orderedRules.findIndex(r => r.Id === selectedRule.Id);
        const prevRule = selectedIndex > 0 ? orderedRules[selectedIndex - 1] : undefined;
        const nextRule = selectedIndex >= 0 && selectedIndex < orderedRules.length - 1 ? orderedRules[selectedIndex + 1] : undefined;

        const assignedNodeIds = new Set<string>(
            this.state.rules.filter(rule => rule.Id !== selectedRule.Id).map(rule => rule.OntologyNodeId)
        );

        const candidates = this.state.nodes.filter(node => {
            // the currently assigned node is always selectable.
            if (node.Id === selectedRule.OntologyNodeId)
                return true;

            // a node already taken by another rule cannot be reused.
            if (assignedNodeIds.has(node.Id))
                return false;

            // must sit below the previous rule's node so the chain above stays intact.
            if (prevRule && !this.isAncestor(prevRule.OntologyNodeId, node.Id))
                return false;

            // must sit above the next rule's node so the rule below isn't orphaned.
            if (nextRule && !this.isAncestor(node.Id, nextRule.OntologyNodeId))
                return false;

            return true;
        });

        return candidates.map(node => ({ value: node.Id, label: node.Name }));
    }

    private getAvailableFilterTypes(): {value: string, label: string}[]
    {
        const filterTypes: {value: string, label: string}[] = [];

        switch (this.state.selectedRule?.RuleType)
        {
            case 'Filter':
                filterTypes.push({ label: this.labels.TreeViewRule_FilterTypes_Expression, value: 'Expression'});
                break;
            default:
                filterTypes.push({ label: this.labels.TreeViewRule_FilterTypes_None, value: 'None'});
                break;
                
        }
        return filterTypes;
    }

    private handleDelete(): Promise<void>
    {
        const {selectedRule} = this.state;
        if (!selectedRule)
            return Promise.resolve();
        
        let i = 0;
        const rules = this.state.rules.filter(r => r.Id !== selectedRule.Id).map(r => {
            r.Order = i++;
            return r;
        });

        this.setState({
            selectedRule: undefined,
            gridRefreshToken: Guid.new().toString(),
            rules: rules
        });

        this.raiseDefinitionUpdate(rules);
        return Promise.resolve();
    }

    private handleAdd(): void
    {
        const [nodeForRule] = this.getAssignableNodesForNewRule();
        if (!nodeForRule)
            return;

        const newRuleIndex = this.state.rules.length;
        const rule = {
            RuleType: this.ruleTypes[0].value,
            FilterType: 'None',
            FilterValue: '',
            KeepEmptyAssets: false,
            UseFullNames: false,
            Order: newRuleIndex,
            Name: '',
            OntologyNodeId: nodeForRule.Id,
            Nested: false,
            Id: Guid.new().toString()
        };

        const { rules } = this.state;
        rules.push(rule);

        this.setState({
            rules: rules,
            selectedRule: rule,
            gridRefreshToken: Guid.new().toString(),
        });
        this.raiseDefinitionUpdate(rules);
    }
    
    private handleCellClicked(i: ITreeViewRule): void 
    {
        const { gridData } = this.state;
        gridData.checkedIds = [ i.Id ];
        this.setState({ gridData, selectedRule: i })
    }

    private handleRuleTypeChange(e: SelectionDetails): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.RuleType = e.value;
        this.setState({
            selectedRule: this.state.selectedRule
        }, () => this.raiseDefinitionUpdate());
    }

    private handleRuleAtLevelChange(e: SelectionDetails): void 
    {
        const { selectedRule } = this.state;
        if (!selectedRule)
            return;

        const node = this.state.nodes.find(n => n.Id === e.value);

        if (!node)
            throw new Error("No available nodes for new rule assignment.");

        if (!this.getAvailableNodes().some(available => available.value === e.value))
            return;

        selectedRule.OntologyNodeId = e.value;
        this.setState({ selectedRule }, () => this.raiseDefinitionUpdate());
    }

    private handleFilterValueChange(e: ChangeEvent<HTMLTextAreaElement, HTMLTextAreaElement>): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.FilterValue = e.target.value;
        this.setState({
            selectedRule: this.state.selectedRule
        }, () => this.raiseDefinitionUpdate());
    }
    private handleFilterTypeChange(e: SelectionDetails): void
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.FilterType = e.value;
        this.setState({
            selectedRule: this.state.selectedRule
        }, () => this.raiseDefinitionUpdate());
    }

    private handleKeepEmptyAssetsChange(e: CheckedChangeDetails): void
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.KeepEmptyAssets = e.checked;
        this.setState({
            selectedRule: this.state.selectedRule
        }, () => this.raiseDefinitionUpdate());
    }

    private handleUseFullNameChange(e: CheckedChangeDetails): void
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.UseFullNames = e.checked;
        this.setState({
            selectedRule: this.state.selectedRule
        }, () => this.raiseDefinitionUpdate());
    }
    
    private handleGridChange(i: IGridData<ITreeViewRule>): void 
    {
        const ids = i.checkedIds;
        if (ids && ids.length > 0)
            this.setState({ gridData: i, selectedRule: this.state.rules.find(r => r.Id === ids[0]) });
        else
            this.setState({ gridData: i, selectedRule: undefined });
    }

    private async handleRowsRequest(request: IGridRowsRequest<ITreeViewRule>): Promise<IGridRowsResponse<ITreeViewRule>>
    {
        return {
            rows: this.state.rules,
            nextSkipToken: ''
        };
    }

    public render(): JSX.Element
    {
        return (
            
            <Card
                title={this.labels.Platform_VirtualTreeView_TreeViewRulesTitle}
                subTitle={this.labels.Platform_VirtualTreeView_TreeViewRulesSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <GridContainer>
                            <GridItem>
                                <ButtonBar>
                                    <RightButtons>
                                        <DeleteButton
                                            small={true}
                                            onDelete={() => this.handleDelete()}
                                            disabled={!this.state.selectedRule}
                                        />
                                        <Button
                                            size='small'
                                            styleType='ghost'
                                            leftIcon={<SerendieSymbol name='plus' />}
                                            onClick={() => this.handleAdd()}
                                            disabled={!this.canAdd}
                                        >
                                            {this.labels.Platform_CommonAction_Add}
                                        </Button>
                                    </RightButtons>
                                </ButtonBar>
                            </GridItem>
                            <GridItem>
                                
                                <DataGrid
                                    columns={[
                                        {
                                            columnId: Helper.nameOf<ITreeViewRule>("RuleType"),
                                            title: this.labels.TreeViewRule_RuleType,
                                            getCellValue: rule => rule.RuleType,
                                            columnWidth: 50,
                                        },
                                        {
                                            columnId: Helper.nameOf<ITreeViewRule>("OntologyNodeId"),
                                            title: this.labels.TreeViewRule_RuleAtLevel,
                                            getCellValue: rule => this.state.nodes.find(n => n.Id === rule.OntologyNodeId)?.Name ?? '',
                                            columnWidth: 50,
                                        }
                                    ]}
                                    data={this.state.gridData}
                                    rowId={i => i.Id}
                                    onChange={i => this.handleGridChange(i)}
                                    onRowsRequest={i => this.handleRowsRequest(i)}
                                    refreshToken={this.state.gridRefreshToken}
                                    onCellClicked={i => this.handleCellClicked(i)}
                                    showCheckBoxes={true}
                                    allowOnlyOneCheckBox={true}

                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {this.state.selectedRule !== undefined && 
                    <GridItem expanded={6}>
                        <GridContainer spacing='xs'>
                            <GridItem>
                                <Select
                                    label={this.labels.TreeViewRule_RuleType}
                                    placeholder={this.labels.TreeViewRule_RuleType}
                                    items={this.ruleTypes.map(x => ({ value: x.value, label: x.label }))}
                                    onSelect={e => this.handleRuleTypeChange(e)}
                                    value={this.state.selectedRule?.RuleType ? [this.state.selectedRule.RuleType] : []}
                                />
                            </GridItem>
                            <GridItem>
                                <Select
                                    label={this.labels.TreeViewRule_RuleAtLevel}
                                    placeholder={this.labels.TreeViewRule_RuleAtLevel}
                                    items={this.getAvailableNodes()}
                                    onSelect={e => this.handleRuleAtLevelChange(e)}
                                    value={this.state.selectedRule?.OntologyNodeId ? [this.state.selectedRule.OntologyNodeId] : []}
                                />
                            </GridItem>
                            <GridItem>
                                { this.state.selectedRule?.RuleType === 'Real' ?
                                    <Select
                                        label={this.labels.TreeViewRule_FilterType}
                                        disabled
                                    />
                                :
                                    <Select
                                        label={this.labels.TreeViewRule_FilterType}
                                        placeholder={this.labels.TreeViewRule_FilterType}
                                        items={this.getAvailableFilterTypes()}
                                        onSelect={e => this.handleFilterTypeChange(e)}
                                        value={this.state.selectedRule?.FilterType ? [this.state.selectedRule.FilterType] : []}
                                        disabled={this.state.selectedRule?.RuleType === 'Real'}
                                    />
                                }
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.TreeViewRule_UseFullNames}
                                    checked={this.state.selectedRule?.UseFullNames}
                                    onCheckedChange={e => this.handleUseFullNameChange(e)}
                                />
                            </GridItem>
                            <GridItem>
                                { this.state.selectedRule?.RuleType === 'Real' ?
                                    <TextField
                                        label={this.labels.TreeViewRule_FilterValue}
                                        disabled
                                    />
                                :
                                    <TextArea
                                        label={this.labels.TreeViewRule_FilterValue}
                                        placeholder={this.labels.TreeViewRule_FilterValue}
                                        value={this.state.selectedRule?.FilterValue ?? ''}
                                        onChange={e => this.handleFilterValueChange(e)}
                                        rows={15}
                                    />
                                }
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.TreeViewRule_KeepEmptyAssets}
                                    checked={this.state.selectedRule?.KeepEmptyAssets}
                                    onCheckedChange={e => this.handleKeepEmptyAssetsChange(e) }
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    }
                </GridContainer>
            </Card>
        )
    }    
}

export default VirtualTreeViewRulesEditor;

export interface IProps
{
    ontologyNodes: IOntologyNode[];
    rules: ITreeViewRulesData;
    onSectionUpdate: OnSectionUpdateHandler;
    onDefinitionUpdate(rules: ITreeViewRule[]): void;
}

export interface IState 
{
    rules: ITreeViewRule[];
    selectedRule?: ITreeViewRule;
    nodes: IOrderedOntologyNode[];
    gridRefreshToken: string;
    gridData: IGridData<ITreeViewRule>;
    
}

export interface IOrderedOntologyNode
{
    Name: string;
    Id: string;
    Level: number;
    ParentId: string | null;
}